import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { FeatureDto } from '@vpfa/rest-api/admin';
import { cloneDeep, xor } from 'lodash';
import { isSameDay, differenceInMilliseconds, formatDuration, intervalToDuration } from 'date-fns';

export type FeatureFlagsState = { loaded: boolean; data: FeatureDto[] };

export let featureFlagsCheckTaskIntervalMs = 30 * 60 * 1000; // 30 mins
export const customFeatureFlagsCheckIntervalLocalStorageKeyName = 'customFeatureFlagsCheckInterval';
export const notificationShownLocalStorageKeyName = 'newAppNotificationShown';
export const lastFeatureFlagsCheckTimeStorageKeyName = 'lastFeatureFlagsCheckTime';
export const reloadAllTabsStorageKeyName = 'reloadAllTabs';

export const ssmNewAppVersionEventName = 'ssmNewAppVersion';
export const ssmNewAppVersionEvent = new CustomEvent(ssmNewAppVersionEventName);

/**
 * This service is checking if Features Flags has changed or new version of Application has been deployed and offers a
 * restart via custom Event.
 *
 * It has been crafted to avoid dependencies, because this service is started with APP_INITIALIZER so it will
 * affect bundle size and performance.
 */
@Injectable({
  providedIn: 'root',
})
export class NewAppVersionService {
  private newDeploymentVersion = false;
  private featureFlagsChanged = false;
  private currentFeatureFlagsState: FeatureFlagsState = { loaded: false, data: [] };
  private featureFlagsDetectionTaskInterval: any | undefined;
  private boundHandleDocumentFocus: () => void;
  private abortSignal = new AbortController();

  constructor() {
    this.loadCustomInterval();

    // to avoid infinite loop of restarts
    window.localStorage.removeItem(reloadAllTabsStorageKeyName);

    // add an event listener to listen to storage events indicating a reload is needed
    window.addEventListener(
      'storage',
      event => {
        if (event.key === reloadAllTabsStorageKeyName && event.newValue !== null) {
          window.localStorage.removeItem(reloadAllTabsStorageKeyName);
          location.reload();
        }
      },
      { signal: this.abortSignal.signal },
    );

    this.initDebugUtils();
  }

  // to avoid using API Service directly and because this service do not need it when user is not logged in
  public featureFlagsGetter: () => Promise<FeatureDto[]>;

  public detectedNewDeploymentVersion() {
    this.newDeploymentVersion = true;
    this.determineWhetherToShowNewVersion('deployment');
  }

  public restartApp() {
    localStorage.removeItem(notificationShownLocalStorageKeyName);
    window.localStorage.setItem(reloadAllTabsStorageKeyName, new Date().toISOString());
    location.reload();
  }

  // for unit tests to remove event handlers
  public destroy(): void {
    this.abortSignal.abort();
  }

  public initFeatureFlags(flags: FeatureDto[]) {
    this.currentFeatureFlagsState.loaded = true;
    this.currentFeatureFlagsState.data = flags;
    this.startFeatureFlagsDetectionTask();
    this.boundHandleDocumentFocus = this.detectVisibilityChange.bind(this);
    window.addEventListener('focus', this.boundHandleDocumentFocus);
  }

  public clearFeatureFlags() {
    // reset to default because different user may have different set of flags.
    this.featureFlagsChanged = false;

    this.currentFeatureFlagsState.loaded = false;
    this.currentFeatureFlagsState.data = [];

    // we need to stop detection task, because flags are based on user context and will return error if user is logged off
    this.stopFeatureFlagsDetectionTask();

    window.removeEventListener('focus', this.boundHandleDocumentFocus);
  }

  init(): void {
    // do not remove this method, it is here to force Angular to create this service
  }

  public startFeatureFlagsDetectionTask() {
    // to cleanup old interval
    this.stopFeatureFlagsDetectionTask();

    this.featureFlagsDetectionTaskInterval = setInterval(() => {
      if (!pageIsVisible()) {
        // tab is not visible, skip check for now
        return;
      }

      const alreadyCheckedInAnotherTab = this.hasRecentlyExecutedFeatureFlagsGetter(featureFlagsCheckTaskIntervalMs);

      if (alreadyCheckedInAnotherTab) {
        if (!environment.production) console.debug('Feature flags already checked in another tab');
        return;
      }

      this.detectNewFeatureFlags('interval');
    }, featureFlagsCheckTaskIntervalMs);
  }

  private determineWhetherToShowNewVersion(source: string) {
    const hasSystemChangeOccurred = this.newDeploymentVersion || this.featureFlagsChanged;

    if (!hasSystemChangeOccurred) {
      return;
    }

    // to display notification once per day and in one browser's tab
    const lastNotification = localStorage.getItem(notificationShownLocalStorageKeyName);

    const showNotification = shouldShowNotification(
      lastNotification,
      pageIsVisible(),
      hasSystemChangeOccurred,
      new Date(),
    );

    if (showNotification) {
      localStorage.setItem(notificationShownLocalStorageKeyName, new Date().toISOString());

      document.dispatchEvent(ssmNewAppVersionEvent);
    }
  }

  private detectVisibilityChange() {
    if (pageIsVisible()) {
      if (!this.hasRecentlyExecutedFeatureFlagsGetter(featureFlagsCheckTaskIntervalMs)) {
        this.detectNewFeatureFlags('visibilityChange:featureFlags');
        this.determineWhetherToShowNewVersion('visibilityChange:deployment');
      }
    }
  }

  private hasRecentlyExecutedFeatureFlagsGetter(intervalMs: number) {
    const timeTolerance = 2000; // for HTTP request delay etc
    const lastCheckTime = localStorage.getItem(lastFeatureFlagsCheckTimeStorageKeyName);

    if (!lastCheckTime) return false;

    const date = new Date(lastCheckTime);

    const diff = Math.abs(differenceInMilliseconds(date, new Date())) + timeTolerance;

    const result = diff < intervalMs;

    return result;
  }

  private stopFeatureFlagsDetectionTask() {
    if (!!this.featureFlagsDetectionTaskInterval) {
      clearInterval(this.featureFlagsDetectionTaskInterval);
    }
  }

  private async detectNewFeatureFlags(source: string) {
    const prevState = cloneDeep(this.currentFeatureFlagsState);

    if (!this.featureFlagsGetter) {
      throw new Error('Feature Flags Getter not defined');
    }

    return this.featureFlagsGetter()
      .then((flags: FeatureDto[]) => {
        this.currentFeatureFlagsState.loaded = true;
        this.currentFeatureFlagsState.data = flags ?? [];

        this.featureFlagsChanged = hasFeatureFlagsChanged(prevState, this.currentFeatureFlagsState);
      })
      .catch(error => {
        console.error('Cannot load Feature Flags to detect changes', error);
      })
      .finally(() => {
        localStorage.setItem(lastFeatureFlagsCheckTimeStorageKeyName, new Date().toISOString());
        this.determineWhetherToShowNewVersion(source);
      });
  }

  private loadCustomInterval() {
    const customInterval = Number(localStorage.getItem(customFeatureFlagsCheckIntervalLocalStorageKeyName));

    if (Number(customInterval) > 4000) {
      console.log(
        `Using custom feature flags check interval: ${formatDuration(intervalToDuration({ start: 0, end: customInterval }))}`,
      );

      featureFlagsCheckTaskIntervalMs = customInterval;
    }
  }

  private initDebugUtils() {
    if (!(window as any).featureFlags) {
      (window as any).featureFlags = {};
    }

    (window as any).featureFlags.checkNow = () => {
      if (!this.featureFlagsGetter) {
        console.error('You need to be logged in.');
        return;
      }

      this.detectNewFeatureFlags('debug').then();
    };
  }
}

export function hasFeatureFlagsChanged(prev: FeatureFlagsState, current: FeatureFlagsState) {
  if (!prev.loaded) {
    // flags were just loaded -> ignore
    return false;
  }

  // convert to strings for easier comparison
  const diff = xor(
    prev.data.map(x => `${x.flagkey}${x.status}`),
    current.data.map(x => `${x.flagkey}${x.status}`),
  );

  return diff.length > 0;
}

export function shouldShowNotification(
  lastNotification: string | null,
  pageIsVisible: boolean,
  hasSystemChangeOccurred: boolean,
  todaysDate: Date,
): boolean {
  const notificationNotYetDisplayed = !lastNotification || !isSameDay(new Date(lastNotification), todaysDate);

  return pageIsVisible && notificationNotYetDisplayed && hasSystemChangeOccurred;
}

export function pageIsVisible() {
  return document.visibilityState === 'visible' && document.hasFocus();
}
